import React, { useEffect, useState } from 'react';
import {
  HIDEABLE_PROFILE_FIELDS,
  HIDEABLE_PROFILE_FIELD_LABEL,
  HideableProfileField,
  useAdminUpdateHiddenProfileFieldsMutation,
} from 'src/store/userApi';
import { Button } from 'src/ui/Button';
import { Card } from 'src/ui/Card';
import { Checkbox } from 'src/ui/Checkbox';
import { useToast } from 'src/ui/Toast';

interface HiddenProfileFieldsProps {
  userId: string;
  hiddenFields: string[] | null | undefined;
  onSaved: (hiddenFields: HideableProfileField[]) => void;
}

const sameFields = (a: string[], b: string[]) =>
  a.length === b.length && a.every((field) => b.includes(field));

const HiddenProfileFields = ({
  userId,
  hiddenFields,
  onSaved,
}: HiddenProfileFieldsProps) => {
  const saved = hiddenFields ?? [];
  const [draft, setDraft] = useState<string[]>(saved);
  const [updateHiddenFields, { isLoading }] =
    useAdminUpdateHiddenProfileFieldsMutation();
  const push = useToast();

  const savedKey = saved.join(',');

  useEffect(() => {
    setDraft(savedKey ? savedKey.split(',') : []);
  }, [userId, savedKey]);

  const toggle = (field: HideableProfileField) =>
    setDraft((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );

  const onSave = async () => {
    const next = HIDEABLE_PROFILE_FIELDS.filter((f) => draft.includes(f));
    try {
      await updateHiddenFields({ id: userId, hiddenFields: next }).unwrap();
      onSaved(next);
      push({ title: 'Hidden fields saved', type: 'success' });
    } catch (error) {
      push({ title: 'Failed to save hidden fields', type: 'error' });
    }
  };

  const isDirty = !sameFields(draft, saved);

  return (
    <div>
      <div
        className="row"
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 'var(--fs-md)',
            color: 'var(--text)',
          }}
        >
          Hidden from profile card
        </span>
        <Button
          size="sm"
          variant="primary"
          onClick={onSave}
          disabled={isLoading || !isDirty}
        >
          {isLoading ? 'Saving…' : 'Save Hidden Fields'}
        </Button>
      </div>
      <Card pad>
        <div
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-secondary)',
            marginBottom: 12,
          }}
        >
          Checked fields are left off this user's profile card in the app, for
          other people and for the user. Edit Profile still shows them.
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 8,
          }}
        >
          {HIDEABLE_PROFILE_FIELDS.map((field) => (
            <label
              key={field}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontSize: 'var(--fs-sm)',
                color: 'var(--text)',
              }}
            >
              <Checkbox
                checked={draft.includes(field)}
                onChange={() => toggle(field)}
              />
              {HIDEABLE_PROFILE_FIELD_LABEL[field]}
            </label>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default HiddenProfileFields;
